// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\ReferralCampaignWizard.jsx
// Название: Referral Campaign Wizard - Simple 2-Step
// Версия: 2.1.0
// Дата: 30.12.2025
// Описание: Простой wizard для создания реферальной программы

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';

// ============================================
// ГЛАВНЫЙ КОМПОНЕНТ
// ============================================

const ReferralCampaignWizard = () => {
  const { id: campaignId } = useParams();
  const navigate = useNavigate();

  // ============================================
  // СОСТОЯНИЕ (STATE)
  // ============================================
  
  // Текущий шаг: 1 = настройки, 2 = результаты
  const [step, setStep] = useState(1);
  
  // Флаги загрузки
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  // Ошибки
  const [error, setError] = useState('');
  
  // Данные кампании
  const [campaign, setCampaign] = useState(null);
  const [gifts, setGifts] = useState([]);

  // Настройки реферальной программы
  const [config, setConfig] = useState({
    referrerReward: 5,   // Награда пригласившему (DAR)
    refereeReward: 10,   // Награда приглашённому (DAR)
    giftCount: 5         // Количество стартовых подарков
  });

  // ============================================
  // ЗАГРУЗКА ДАННЫХ
  // ============================================
  
  useEffect(() => {
    loadData();
  }, [campaignId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Загружаем данные кампании
      const campRes = await api.get(`/campaigns/${campaignId}`);
      const campaignData = campRes.data || campRes;
      setCampaign(campaignData);

      // Загружаем подарки кампании
      const giftsRes = await api.get(`/brand-gifts/campaign/${campaignId}/gifts`);
      const giftsData = giftsRes.data?.gifts || giftsRes.gifts || [];
      setGifts(giftsData);

      // Если есть готовые подарки с картинками - показываем результаты
      const hasReadyGifts = giftsData.some(g => g.status === 'ready' && g.image_url);
      if (hasReadyGifts) {
        setStep(2);
      }
    } catch (err) {
      console.error('Load error:', err);
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // ОБРАБОТЧИКИ ДЕЙСТВИЙ
  // ============================================

  // Генерация подарков
  const handleGenerate = async () => {
    setGenerating(true);
    setError('');

    try {
      // Шаг 1: Сохраняем конфигурацию реферальной программы
      await api.put(`/campaigns/${campaignId}`, {
        referral_config: {
          referrer_reward: config.referrerReward,
          referee_reward: config.refereeReward,
          configured: true
        }
      });

      // Шаг 2: Создаём seed-подарки
      await api.post('/brand-gifts/create-seeds', {
        campaignId: parseInt(campaignId),
        count: config.giftCount,
        totalAmount: config.refereeReward,
        aiStyle: 'referral',
        sourceType: 'referral'
      });

      // Шаг 3: Генерируем AI картинки
      await api.post(`/brand-gifts/campaign/${campaignId}/generate-all`, {
        aiStyle: 'referral',
        brandName: campaign?.name || 'DARY',
        brandUrl: 'dary.one',
        campaignType: 'referral',
        lang: 'ru'
      });

      // Шаг 4: Обновляем данные и переходим к результатам
      await loadData();
      setStep(2);
      
    } catch (err) {
      console.error('Generate error:', err);
      setError(err.response?.data?.error || 'Ошибка генерации');
    } finally {
      setGenerating(false);
    }
  };

  // Удаление кампании
  const handleDelete = async () => {
    const confirmed = window.confirm(
      'Удалить кампанию?\n\nНеиспользованные средства вернутся на баланс.'
    );
    if (!confirmed) return;

    try {
      await api.delete(`/campaigns/${campaignId}`);
      navigate('/business/brand/campaigns');
    } catch (err) {
      console.error('Delete error:', err);
      alert('Ошибка удаления кампании');
    }
  };

  // Скачивание картинки подарка
  const downloadImage = (gift) => {
    const code = gift.shortCode || gift.short_code || gift.gift_code || gift.id;
    const link = document.createElement('a');
    link.href = gift.image_url;
    link.download = `referral_${code}.png`;
    link.click();
  };

  // Копирование кода в буфер
  const copyCode = (gift) => {
    const code = gift.shortCode || gift.short_code || gift.gift_code;
    if (code) {
      navigator.clipboard.writeText(code);
      alert(`Код скопирован: ${code}`);
    }
  };

  // Получение кода подарка (с учётом разных форматов API)
  const getGiftCode = (gift) => {
    return gift.shortCode || gift.short_code || gift.gift_code || '----';
  };

  // ============================================
  // СТИЛИ
  // ============================================
  
  const styles = {
    // Контейнер
    container: { 
      padding: 24, 
      maxWidth: 900, 
      margin: '0 auto' 
    },

    // Шапка
    header: { 
      textAlign: 'center', 
      marginBottom: 32 
    },
    badge: { 
      display: 'inline-block', 
      background: '#3B82F6', 
      color: 'white', 
      padding: '6px 16px', 
      borderRadius: 20, 
      fontSize: 12, 
      fontWeight: 600, 
      marginBottom: 8 
    },
    title: { 
      fontSize: 28, 
      fontWeight: 700, 
      margin: '8px 0' 
    },
    subtitle: { 
      color: '#6b7280' 
    },

    // Карточка
    card: { 
      background: 'white', 
      borderRadius: 16, 
      padding: 32, 
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)', 
      marginBottom: 24 
    },
    cardTitle: { 
      fontSize: 18, 
      fontWeight: 600, 
      marginBottom: 24, 
      display: 'flex', 
      alignItems: 'center', 
      gap: 8 
    },

    // Блоки наград
    rewardRow: { 
      display: 'flex', 
      gap: 24, 
      marginBottom: 24 
    },
    rewardBox: { 
      flex: 1, 
      background: '#f0f9ff', 
      borderRadius: 16, 
      padding: 24, 
      textAlign: 'center' 
    },
    rewardBoxGreen: { 
      flex: 1, 
      background: '#f0fdf4', 
      borderRadius: 16, 
      padding: 24, 
      textAlign: 'center' 
    },
    rewardIcon: { 
      fontSize: 40, 
      marginBottom: 8 
    },
    rewardLabel: { 
      fontSize: 13, 
      color: '#6b7280', 
      marginBottom: 8 
    },
    rewardInput: { 
      width: 100, 
      padding: '12px', 
      fontSize: 24, 
      fontWeight: 700, 
      textAlign: 'center', 
      border: '2px solid #e5e7eb', 
      borderRadius: 12 
    },
    rewardUnit: { 
      fontSize: 14, 
      color: '#6b7280', 
      marginTop: 4 
    },

    // Строка количества
    countRow: { 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      gap: 16, 
      padding: 20, 
      background: '#f9fafb', 
      borderRadius: 12 
    },
    countLabel: { 
      fontSize: 15, 
      color: '#374151' 
    },
    countInput: { 
      width: 80, 
      padding: '10px', 
      fontSize: 18, 
      fontWeight: 600, 
      textAlign: 'center', 
      border: '2px solid #e5e7eb', 
      borderRadius: 10 
    },

    // Подсказка
    hint: { 
      background: '#fef3c7', 
      padding: 16, 
      borderRadius: 12, 
      marginTop: 16 
    },
    hintText: { 
      color: '#92400e', 
      fontSize: 14 
    },

    // Кнопки
    actions: { 
      display: 'flex', 
      gap: 12, 
      marginTop: 24 
    },
    btn: { 
      flex: 1, 
      padding: '16px 24px', 
      borderRadius: 12, 
      fontSize: 16, 
      fontWeight: 600, 
      cursor: 'pointer', 
      border: 'none', 
      transition: 'all 0.2s' 
    },
    btnPrimary: { 
      background: '#10B981', 
      color: 'white' 
    },
    btnDanger: { 
      background: '#fee2e2', 
      color: '#dc2626' 
    },
    btnSecondary: { 
      background: '#f3f4f6', 
      color: '#374151' 
    },
    btnDisabled: { 
      opacity: 0.6, 
      cursor: 'not-allowed' 
    },

    // Статистика
    stats: { 
      display: 'grid', 
      gridTemplateColumns: 'repeat(4, 1fr)', 
      gap: 16, 
      marginBottom: 24 
    },
    stat: { 
      background: '#f9fafb', 
      borderRadius: 12, 
      padding: 16, 
      textAlign: 'center' 
    },
    statValue: { 
      fontSize: 24, 
      fontWeight: 700 
    },
    statLabel: { 
      fontSize: 12, 
      color: '#6b7280', 
      marginTop: 4 
    },

    // Сетка подарков
    giftsGrid: { 
      display: 'grid', 
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
      gap: 16 
    },
    giftCard: { 
      background: '#f9fafb', 
      borderRadius: 12, 
      overflow: 'hidden' 
    },
    giftImage: { 
      width: '100%', 
      aspectRatio: '1', 
      objectFit: 'cover' 
    },
    giftInfo: { 
      padding: 12 
    },
    giftCode: { 
      fontSize: 14, 
      fontWeight: 700, 
      fontFamily: 'monospace', 
      color: '#111' 
    },
    giftAmount: { 
      fontSize: 13, 
      color: '#10B981', 
      fontWeight: 600 
    },
    giftBtns: { 
      display: 'flex', 
      gap: 8, 
      marginTop: 8 
    },
    giftBtn: { 
      flex: 1, 
      padding: '8px', 
      borderRadius: 8, 
      border: 'none', 
      cursor: 'pointer', 
      fontSize: 12 
    },

    // Ошибка
    error: { 
      background: '#fef2f2', 
      color: '#dc2626', 
      padding: 16, 
      borderRadius: 12, 
      marginBottom: 16, 
      textAlign: 'center' 
    },

    // Загрузка
    loading: { 
      textAlign: 'center', 
      padding: 60 
    },
    spinner: { 
      fontSize: 48, 
      marginBottom: 16 
    }
  };

  // ============================================
  // РЕНДЕР: ЗАГРУЗКА
  // ============================================
  
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={styles.spinner}>⏳</div>
          <p>Загрузка...</p>
        </div>
      </div>
    );
  }

  // ============================================
  // ВЫЧИСЛЕНИЯ
  // ============================================
  
  // Готовые подарки с картинками
  const readyGifts = gifts.filter(g => g.status === 'ready' && g.image_url);
  
  // Итоговая стоимость
  const totalCost = config.giftCount * config.refereeReward;

  // ============================================
  // РЕНДЕР: ОСНОВНОЙ
  // ============================================
  
  return (
    <div style={styles.container}>
      
      {/* ===== ШАПКА ===== */}
      <div style={styles.header}>
        <div style={styles.badge}>🔗 REFERRAL</div>
        <h1 style={styles.title}>{campaign?.name || 'Реферальная программа'}</h1>
        <p style={styles.subtitle}>Приглашай друзей — получай награды</p>
      </div>

      {/* ===== ОШИБКА ===== */}
      {error && <div style={styles.error}>❌ {error}</div>}

      {/* ===== ШАГ 1: НАСТРОЙКИ ===== */}
      {step === 1 && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>⚙️ Настройка наград</h3>

          {/* Блоки наград */}
          <div style={styles.rewardRow}>
            
            {/* Награда пригласившему */}
            <div style={styles.rewardBox}>
              <div style={styles.rewardIcon}>👤</div>
              <div style={styles.rewardLabel}>Пригласивший получит</div>
              <input
                type="number"
                style={styles.rewardInput}
                value={config.referrerReward}
                onChange={(e) => setConfig({ 
                  ...config, 
                  referrerReward: parseInt(e.target.value) || 0 
                })}
                min="1"
              />
              <div style={styles.rewardUnit}>DAR за друга</div>
            </div>

            {/* Награда приглашённому */}
            <div style={styles.rewardBoxGreen}>
              <div style={styles.rewardIcon}>🎁</div>
              <div style={styles.rewardLabel}>Приглашённый получит</div>
              <input
                type="number"
                style={styles.rewardInput}
                value={config.refereeReward}
                onChange={(e) => setConfig({ 
                  ...config, 
                  refereeReward: parseInt(e.target.value) || 0 
                })}
                min="1"
              />
              <div style={styles.rewardUnit}>DAR при активации</div>
            </div>
          </div>

          {/* Количество подарков */}
          <div style={styles.countRow}>
            <span style={styles.countLabel}>Создать</span>
            <input
              type="number"
              style={styles.countInput}
              value={config.giftCount}
              onChange={(e) => setConfig({ 
                ...config, 
                giftCount: parseInt(e.target.value) || 1 
              })}
              min="1"
              max="50"
            />
            <span style={styles.countLabel}>стартовых подарков</span>
          </div>

          {/* Подсказка с итогом */}
          <div style={styles.hint}>
            <div style={styles.hintText}>
              💡 <strong>Итого:</strong> {config.giftCount} × {config.refereeReward} DAR = <strong>{totalCost} DAR</strong> + награды за приглашения
            </div>
          </div>

          {/* Кнопки действий */}
          <div style={styles.actions}>
            <button
              style={{ ...styles.btn, ...styles.btnDanger }}
              onClick={handleDelete}
            >
              🗑️ Удалить
            </button>
            <button
              style={{ 
                ...styles.btn, 
                ...styles.btnPrimary, 
                ...(generating ? styles.btnDisabled : {}) 
              }}
              onClick={handleGenerate}
              disabled={generating}
            >
              {generating ? '⏳ Генерация...' : '🚀 Создать подарки'}
            </button>
          </div>
        </div>
      )}

      {/* ===== ШАГ 2: РЕЗУЛЬТАТЫ ===== */}
      {step === 2 && (
        <>
          {/* Статистика */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>📊 Аналитика</h3>
            <div style={styles.stats}>
              <div style={styles.stat}>
                <div style={{ ...styles.statValue, color: '#3B82F6' }}>
                  {readyGifts.length}
                </div>
                <div style={styles.statLabel}>🎁 Подарков</div>
              </div>
              <div style={styles.stat}>
                <div style={{ ...styles.statValue, color: '#10B981' }}>0</div>
                <div style={styles.statLabel}>✅ Активировано</div>
              </div>
              <div style={styles.stat}>
                <div style={styles.statValue}>0</div>
                <div style={styles.statLabel}>📈 K-factor</div>
              </div>
              <div style={styles.stat}>
                <div style={{ ...styles.statValue, color: '#f59e0b' }}>0</div>
                <div style={styles.statLabel}>💰 Выплачено</div>
              </div>
            </div>
          </div>

          {/* Список подарков */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>🎁 Подарки ({readyGifts.length})</h3>

            <div style={styles.giftsGrid}>
              {readyGifts.map((gift) => (
                <div key={gift.id} style={styles.giftCard}>
                  
                  {/* Картинка */}
                  <img 
                    src={gift.image_url} 
                    alt={getGiftCode(gift)} 
                    style={styles.giftImage} 
                  />
                  
                  {/* Информация */}
                  <div style={styles.giftInfo}>
                    <div style={styles.giftCode}>{getGiftCode(gift)}</div>
                    <div style={styles.giftAmount}>
                      {parseFloat(gift.total_amount || gift.totalAmount).toFixed(0)} DAR
                    </div>
                    
                    {/* Кнопки */}
                    <div style={styles.giftBtns}>
                      <button
                        style={{ 
                          ...styles.giftBtn, 
                          background: '#3B82F6', 
                          color: 'white' 
                        }}
                        onClick={() => downloadImage(gift)}
                        title="Скачать"
                      >
                        ⬇️
                      </button>
                      <button
                        style={{ 
                          ...styles.giftBtn, 
                          background: '#e5e7eb' 
                        }}
                        onClick={() => copyCode(gift)}
                        title="Копировать код"
                      >
                        📋
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Кнопки управления */}
            <div style={styles.actions}>
              <button
                style={{ ...styles.btn, ...styles.btnDanger }}
                onClick={handleDelete}
              >
                🗑️ Удалить
              </button>
              <button
                style={{ ...styles.btn, ...styles.btnSecondary }}
                onClick={() => setStep(1)}
              >
                ⚙️ Настройки
              </button>
              <button
                style={{ ...styles.btn, ...styles.btnSecondary }}
                onClick={() => navigate('/business/brand/campaigns')}
              >
                ← К списку
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ReferralCampaignWizard;